<template>
  <div class="notification-list">
    <div
      v-if="loading && notifications.length === 0"
      class="notification-list__state">
      <q-spinner
        color="primary"
        size="md" />
      <div class="notification-list__state-text">{{ t('loading.notifications') }}</div>
    </div>

    <div
      v-else-if="!loading && notifications.length === 0"
      class="notification-list__state notification-list__state--empty">
      <div class="notification-list__empty-icon-wrap">
        <q-icon
          :name="symRoundedNotifications"
          class="notification-list__empty-icon" />
      </div>
      <div class="notification-list__state-title">{{ t('empty.title') }}</div>
      <div class="notification-list__state-text">{{ t('empty.message') }}</div>
    </div>

    <div
      v-else
      class="notification-list__items">
      <NotificationItem
        v-for="notification in notifications"
        :key="notification.id"
        :notification="notification"
        @click="$emit('notification-click', notification)"
        @mark-read="$emit('mark-read', notification)"
        @mark-unread="$emit('mark-unread', notification)"
        @delete="$emit('delete', notification)"
        @execute-action="(n, actionName, action) => $emit('execute-action', n, actionName, action)"
        @retry="$emit('retry', notification)"
        @cancel="$emit('cancel', notification)" />
    </div>

    <div
      v-if="hasMore"
      class="notification-list__more">
      <button
        v-if="!loading"
        type="button"
        class="notification-list__more-btn"
        @click="$emit('load-more')">
        {{ t('actions.loadMore') }}
      </button>
      <div
        v-else
        class="notification-list__state">
        <q-spinner
          color="primary"
          size="sm" />
        <span class="notification-list__state-text">{{ t('loading.more') }}</span>
      </div>
    </div>

    <q-intersection
      v-if="hasMore && !loading"
      class="notification-list__scroll-trigger"
      @intersection="$emit('load-more')" />
  </div>
</template>

<script setup lang="ts">
import { symRoundedNotifications } from '@quasar/extras/material-symbols-rounded'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import type { Notification, NotificationAction } from '../types'
import NotificationItem from './NotificationItem.vue'

interface Props {
  notifications: Notification[]
  loading?: boolean
  hasMore?: boolean
}

interface Emits {
  (e: 'notification-click', notification: Notification): void
  (e: 'mark-read', notification: Notification): void
  (e: 'mark-unread', notification: Notification): void
  (e: 'delete', notification: Notification): void
  (e: 'execute-action', notification: Notification, actionName: string, action: NotificationAction): void
  (e: 'retry', notification: Notification): void
  (e: 'cancel', notification: Notification): void
  (e: 'load-more'): void
}

withDefaults(defineProps<Props>(), {
  loading: false,
  hasMore: false,
})

defineEmits<Emits>()
const { t } = useCompositeI18n('core.NotificationCenter')
</script>

<style lang="scss" scoped>
.notification-list {
  height: 100%;
  overflow-y: auto;
  padding: 6px 8px 12px;
}

.notification-list::-webkit-scrollbar {
  width: 6px;
}

.notification-list::-webkit-scrollbar-track {
  background: transparent;
}

.notification-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 3px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

.body--light .notification-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.18);

  &:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}

.notification-list__items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notification-list__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 28px 16px;
  text-align: center;
}

.notification-list__state--empty {
  padding: 40px 16px;
}

.notification-list__empty-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 4px;
}

.body--light .notification-list__empty-icon-wrap {
  background: rgba(0, 0, 0, 0.04);
}

.notification-list__empty-icon {
  font-size: 1.4rem;
  opacity: 0.55;
}

.notification-list__state-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: $grey-5;
}

.notification-list__state-text {
  font-size: 0.72rem;
  color: $grey-6;
}

.notification-list__more {
  display: flex;
  justify-content: center;
  padding: 10px 8px;
}

.notification-list__more-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: var(--q-primary);
  font-size: 0.74rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: color-mix(in srgb, var(--q-primary) 10%, transparent);
    border-color: color-mix(in srgb, var(--q-primary) 35%, transparent);
  }
}

.body--light .notification-list__more-btn {
  border-color: rgba(0, 0, 0, 0.12);
}

.notification-list__scroll-trigger {
  height: 8px;
}
</style>
